import { Options, Vue } from 'vue-class-component';
import { nextTick, PropType } from 'vue';
import { mapState } from 'vuex';
import FieldValidator from '@/views/calculators/services/fieldValidatorService';
import { TravelSettings } from '../../travel/TravelSettings';
import ExistingABProductsComponent from '../../components/ExistingABProductsComponent.vue';
import store from '@/store/store';
import { CardBlock } from '@/cms/definitions/content-types';
import { jaNejArray, Validator, ValuePair } from '../../services/ValidationService';
import { capitalizeFirstLetter, handleZipCodePrefill } from '../../services/UiStepService';
import { TravelModel } from '../TravelModel';
import { CustomerCalculation } from '../../types/types';
import { handleDawaAddressSearch } from '@/views/calculators/services/addressMappingService';

@Options({
	name: 'PersonInfoStepComponent',
	props: {
		card: Object as PropType<CardBlock>,
		isValid: Boolean,
		hasValidAge: Boolean,
	},
	components: {
		ExistingABProductsComponent,
	},
	computed: mapState<any>({
		model: (state) => state.calculatorContext[state.calculatorContext.active].model,
		cms: (state) => state.calculatorContext[state.calculatorContext.active].cms,
	}),
})
export default class PersonInfoStepComponent extends Vue {
	card: CardBlock; // Card from cms
	isValid: boolean; // whether card is complete
	hasValidAge: boolean;

	public model!: TravelModel; // datastructure for user input (store)
	public cms!: TravelSettings; // settings from CMS (store)

	public fieldValidator: FieldValidator = Validator;
	public readonlyAge: string = null;
	public readonlyFamilyMemberCount: string = null;
	public showChooseAge = true;

	public jaNejArray: Array<ValuePair> = jaNejArray;

	public defaultAddress: string = null;

	public created() {
		// klk - maybe move to BuyTravelComponent
		// KLK - if params already set, don't use query params
		try {
			let zipParam = (this.$route.query.zipCode as string) || (this.$route.query.zipcode as string);
			if (zipParam) {
				this.model.personInfo.zipCode = zipParam;
			}
			zipParam = (this.$route.query.zipName as string) || (this.$route.query.zipname as string);
			if (zipParam) {
				this.model.personInfo.zipName = zipParam;
			}

			const customerAge = (this.$route.query.customerAge as string) || (this.$route.query.customerage as string);
			if (customerAge) {
				this.model.personInfo.customerAge = customerAge;
			}

			const familyMemberCount =
				(this.$route.query.familyMemberCount as string) ??
				(this.$route.query.familymembercount as string) ??
				(this.$route.query.persons as string);
			if (familyMemberCount) {
				this.model.personInfo.familyMemberCountId = Number.parseInt(familyMemberCount);
				this.model.personInfo.familyMemberCount =
					this.familyMemberList[this.model.personInfo.familyMemberCountId - 1];
			}
			let destination = this.$route.query.destination as string;
			if (destination) {
				destination = capitalizeFirstLetter(destination.toLowerCase());
				if (destination === 'Verden' || destination === 'Europa') {
					this.model.choosePackage.segmentedProductProperty = destination;
				}
			}

			const addressParam = this.$route.query.address as string;
			if (addressParam) {
				this.defaultAddress = addressParam;
			}

			// klk travel insurance query params
			// country
			// city
		} catch (e) {
			// do nothing, params might be off
		}
	}

	public mounted() {
		handleZipCodePrefill(this.model.personInfo, this.cms);

		if (this.model.multipleProducts) {
			const commonModel = store.getters.getCalculatorCommonModel;
			if (commonModel.personInfo.familyMemberCount) {
				this.readonlyFamilyMemberCount = this.model.personInfo.familyMemberCount;
			}
			if (this.fieldValidator.isValidAge(commonModel?.personInfo?.customerAge)) {
				this.readonlyAge = this.model.personInfo.customerAge;
			}
		}

		const customerCalculation: CustomerCalculation = store.getters.getCustomerCalculation;
		if (
			!this.readonlyAge &&
			customerCalculation.hasCustomerData &&
			this.fieldValidator.isValidAge(customerCalculation?.customer?.personInfo?.customerAge)
		) {
			this.readonlyAge = customerCalculation.customer.personInfo.customerAge;
		}

		this.showChooseAge = this.model.personInfo.familyMemberCountId != 1;
		if (!this.showChooseAge) {
			this.model.personInfo.isOldestInHousehold = 'ja';
		}

		addEventListener('keyup', this.moveOn);
	}

	public beforeUnmount() {
		removeEventListener('keyup', this.moveOn);
	}

	public moveOn(evt) {
		if (evt.keyCode === 13 && this.isValid) {
			this.nextStep();
		}
	}

	public nextStep() {
		this.$emit('nextStep', this.card.routePath);
	}

	public async addressSearch(address) {
		if (await handleDawaAddressSearch(this.model.personInfo, this.model.personInfo.homeAddress, address)) {
			this.model.pristineStep.set(this.card.name, true);
			return;
		}
	}

	public familyMemberSelected(result: any) {
		if (result?.selectedValue) {
			this.model.personInfo.familyMemberCount = result.selectedValue;
			const inx = this.familyMemberList.indexOf(result.selectedValue);
			this.model.personInfo.familyMemberCountId =
				this.model.calculation.abCalc.familyMemberCountOptions[inx].value;

			if (this.model.personInfo.familyMemberCountId == 1) {
				this.model.personInfo.isOldestInHousehold = 'ja';
				this.showChooseAge = false;
				nextTick(() => {
					// wait for transition to begin showing customerAge field
					setTimeout(() => {
						const input = document.getElementById('customerAge');
						if (input) {
							input.focus();
						}
					}, 300);
				});
			} else {
				this.showChooseAge = true;
				if (this.model.personInfo.isOldestInHousehold === 'ja') {
					this.model.personInfo.isOldestInHousehold = undefined;
				}
			}

			return;
		}

		this.model.personInfo.familyMemberCount = undefined;
		this.model.personInfo.familyMemberCountId = undefined;
	}

	get familyMemberList(): Array<string> {
		const memberListFormatted = [];
		this.model.calculation.abCalc.familyMemberCountOptions.forEach((element) => {
			memberListFormatted.push(element.key);
		});
		return memberListFormatted;
	}

	public chooseAge(evt) {
		if (evt?.value) {
			this.model.personInfo.isOldestInHousehold = evt.value;

			nextTick(() => {
				// wait for transition to begin showing customerAge/oldestAge field
				const fieldId =
					this.model.personInfo.isOldestInHousehold === 'ja' ? 'customerAge' : 'oldestInHouseholdAge';
				setTimeout(() => {
					const input = document.getElementById(fieldId);
					if (input) {
						input.focus();
					}
				}, 300);
			});
		}
	}

	public get showExistingProducts(): boolean {
		return (
			this.model.showExistingAlmBrandProducts &&
			this.model.personInfo.zipCode &&
			this.model.personInfo.zipName &&
			this.model.personInfo.familyMemberCount &&
			this.hasValidAge
		);
	}
}
